import React, { useEffect, useRef, useState } from "react";
import {
  MdSearch,
  MdAddCircleOutline,
  MdVisibility,
  MdCheckCircleOutline,
  MdArrowUpward,
  MdArrowDownward,
} from "react-icons/md";
import Card from "../../../../components/card";
import Modal from "../../../../components/modal";
import ModalAddProject from "./ModalAddProject";
import ModalViewDetailProject from "./ModalViewDetailProject";
import SkeletonTable from "../../../../components/skeleton/table";
import api from "../../../../services/axios";

// ─── Helpers ───────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<number, { label: string; cls: string }> = {
  1: { label: "Ativo", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  2: { label: "Concluído", cls: "bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-gray-300" },
  3: { label: "Cancelado", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};

const StatusBadge: React.FC<{ status: number }> = ({ status }) => {
  const s = STATUS_STYLES[status] ?? {
    label: "—",
    cls: "bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-gray-300",
  };
  return (
    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
};

const BILLING_TYPE_LABELS: Record<number, string> = {
  1: "Fixed Price",
  2: "Hourly Rate",
  3: "Recurring",
};

const BILLING_TYPE_CHIP: Record<number, string> = {
  1: "bg-dv-blue-500/10 text-dv-blue-700 dark:bg-dv-blue-500/15 dark:text-dv-blue-300",
  2: "bg-dv-cyan-500/10 text-dv-cyan-700 dark:bg-dv-cyan-500/15 dark:text-dv-cyan-300",
  3: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
};

const BillingTypeBadge: React.FC<{ type?: number }> = ({ type }) => {
  if (!type) return <span className="text-gray-700 dark:text-gray-300">—</span>;
  const cls =
    BILLING_TYPE_CHIP[type] ?? "bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-gray-300";
  return (
    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${cls}`}>
      {BILLING_TYPE_LABELS[type] ?? "—"}
    </span>
  );
};

const formatDateDisplay = (iso?: string) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR");
};

const transformRow = (project: any) => ({
  ...project,
  startDateDisplay: formatDateDisplay(project.startDate),
  estimatedEndDateDisplay: formatDateDisplay(project.estimatedEndDate),
  billingTypeDisplay: BILLING_TYPE_LABELS[project.billingType] ?? "—",
});

// ─── Types ─────────────────────────────────────────────────────────────────

interface TableColumn {
  key: string;
  header: string;
}

interface TableRow {
  [key: string]: string | number;
}

interface ProjectDetails {
  id: number;
  projectName: string;
  startDate?: string;
  estimatedEndDate?: string;
  dtCreated?: string;
  billingType?: number;
  amount?: number | null;
  billingChannels?: (number | { channel: number })[];
}

interface ResponsiveTableProps {
  columns: TableColumn[];
  data: TableRow[];
  initialOpenProjectId?: number | null;
}

// ─── Component ─────────────────────────────────────────────────────────────

const ProjectManagementTable: React.FC<ResponsiveTableProps> = ({
  initialOpenProjectId,
  columns,
  data,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortedColumn, setSortedColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [openAddProjectModal, setOpenAddProjectModal] = useState(false);
  const [openProjectDetailModal, setProjectDetailModal] = useState(false);
  const [projectDetail, setProjectDetail] = useState<ProjectDetails | null>(null); // ← tipagem correta
  const [loading, setLoading] = useState(false);
  const [projectDataTable, setProjectDataTable] = useState<TableRow[]>(data);
  const [confirmFinalize, setConfirmFinalize] = useState<TableRow | null>(null);
  const [finalizeLoading, setFinalizeLoading] = useState(false);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSort = (columnKey: string) => {
    setSortedColumn(columnKey);
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleRowClick = (row: TableRow) => {
    setProjectDetail(row as unknown as ProjectDetails);
    setProjectDetailModal(true);
  };

  // Auto-abre o cadastro de um projeto quando navegado a partir da tela financeira.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current || initialOpenProjectId == null) return;
    const row = projectDataTable.find((r) => r.id === initialOpenProjectId);
    if (row) {
      autoOpenedRef.current = true;
      handleRowClick(row);
    }
  }, [initialOpenProjectId, projectDataTable]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await api.get("/Project/Projects");
      setProjectDataTable(response.data.map(transformRow));
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = (row: TableRow) => setConfirmFinalize(row);

  const handleFinalizeConfirm = async () => {
    if (!confirmFinalize) return;
    setFinalizeLoading(true);
    try {
      await api.patch(`/Project/${confirmFinalize.id}/status`, { status: 2 });
      setProjectDataTable((prev) =>
        prev.map((r) =>
          r.id === confirmFinalize.id
            ? { ...r, status: 2, statusName: "Completed" }
            : r
        )
      );
      setConfirmFinalize(null);
    } catch (error) {
      console.error("Error finalizing project:", error);
    } finally {
      setFinalizeLoading(false);
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────

  const filteredData = projectDataTable.filter((row) =>
    Object.values(row).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Projetos ativos (status 1) sempre primeiro; dentro do grupo, ordenação por coluna (ou ordem natural).
  const sortedData = [...filteredData].sort((a, b) => {
    const byActive = Number(b.status === 1) - Number(a.status === 1);
    if (byActive !== 0) return byActive;
    if (!sortedColumn) return 0;
    return sortOrder === "asc"
      ? a[sortedColumn] > b[sortedColumn] ? 1 : -1
      : a[sortedColumn] < b[sortedColumn] ? 1 : -1;
  });

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <SkeletonTable active />;

  return (
    <div className="animate-fade-up">
      <Card extra="p-5">
        {/* Toolbar */}
        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          {/* Search */}
          <div className="flex flex-1 flex-col gap-1.5">
            <label
              htmlFor="project-search"
              className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              Buscar
            </label>
            <div className="relative">
              <MdSearch
                className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                id="project-search"
                type="text"
                placeholder="Buscar por projeto, cliente ou faturamento…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpenAddProjectModal(true)}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            <MdAddCircleOutline className="h-4 w-4" aria-hidden="true" />
            Adicionar projeto
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-navy-900">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                  >
                    <button
                      type="button"
                      onClick={() => handleSort(column.key)}
                      className="flex items-center gap-1 uppercase transition-colors hover:text-dv-blue-600 dark:hover:text-dv-cyan-300"
                    >
                      {column.header}
                      {sortedColumn === column.key &&
                        (sortOrder === "asc" ? (
                          <MdArrowUpward className="h-3.5 w-3.5" aria-hidden="true" />
                        ) : (
                          <MdArrowDownward className="h-3.5 w-3.5" aria-hidden="true" />
                        ))}
                    </button>
                  </th>
                ))}
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                >
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + 2}
                    className="px-4 py-12 text-center text-sm text-gray-700 dark:text-gray-300"
                  >
                    Nenhum projeto encontrado.
                  </td>
                </tr>
              ) : (
                sortedData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                  >
                    {columns.map((column, colIndex) => (
                      <td
                        key={column.key}
                        className={`whitespace-nowrap px-4 py-3 ${
                          colIndex === 0
                            ? "font-semibold text-navy-700 dark:text-white"
                            : "text-gray-700 dark:text-gray-300"
                        } ${
                          column.key === "startDateDisplay" ||
                          column.key === "estimatedEndDateDisplay"
                            ? "tabular-nums"
                            : ""
                        }`}
                      >
                        {column.key === "billingTypeDisplay" ? (
                          <BillingTypeBadge type={row.billingType as number} />
                        ) : (
                          <span
                            className="block max-w-[220px] truncate"
                            title={String(row[column.key] ?? "")}
                          >
                            {row[column.key]}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-4 py-3">
                      <StatusBadge status={row.status as number} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {(row.status as number) === 1 && (
                          <button
                            type="button"
                            aria-label={`Finalizar projeto ${row.projectName}`}
                            title="Finalizar projeto"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFinalize(row);
                            }}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-amber-200 px-3 py-2 text-xs font-semibold text-amber-700 transition-all hover:bg-amber-50 active:scale-[.98] dark:border-amber-500/30 dark:text-amber-300 dark:hover:bg-amber-900/20"
                          >
                            <MdCheckCircleOutline className="h-4 w-4" aria-hidden="true" />
                            <span className="hidden sm:inline">Finalizar</span>
                          </button>
                        )}
                        <button
                          type="button"
                          aria-label={`Ver detalhes de ${row.projectName}`}
                          title="Ver detalhes"
                          onClick={() => handleRowClick(row)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition-all hover:border-dv-blue-500 hover:text-dv-blue-600 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:text-dv-cyan-300"
                        >
                          <MdVisibility className="h-4 w-4" aria-hidden="true" />
                          <span className="hidden sm:inline">Detalhes</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modals */}
      <ModalAddProject
        openAddProjectModal={openAddProjectModal}
        setOpenAddProjectModal={setOpenAddProjectModal}
        updateState={fetchProjects}
      />

      {projectDetail && (
        <ModalViewDetailProject
          openProjectDetailModal={openProjectDetailModal}
          setOpenProjectDetailModal={setProjectDetailModal}
          details={projectDetail}
          updateState={fetchProjects} // ← estava faltando
        />
      )}

      {/* ── Confirmation modal: Finalizar Projeto ── */}
      {confirmFinalize && (
        <Modal onClose={() => !finalizeLoading && setConfirmFinalize(null)}>
          <div
            role="document"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-[20px] border border-gray-100 bg-white p-6 shadow-dv-lg dark:border-white/10 dark:bg-navy-800"
          >
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              <MdCheckCircleOutline className="h-6 w-6" aria-hidden="true" />
            </div>
            <h3 className="mb-2 text-base font-bold text-navy-700 dark:text-white">
              Finalizar projeto
            </h3>
            <p className="mb-6 text-sm text-gray-700 dark:text-gray-300">
              Deseja marcar o projeto{" "}
              <strong className="text-navy-700 dark:text-white">
                {confirmFinalize.projectName as string}
              </strong>{" "}
              como finalizado?
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmFinalize(null)}
                disabled={finalizeLoading}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleFinalizeConfirm}
                disabled={finalizeLoading}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
              >
                {finalizeLoading ? "Processando…" : "Confirmar"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProjectManagementTable;
